import { useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useVenue } from "../lib/venue";
import { joinMailingList } from "../lib/api";
import { pushCtaClick, pushFormInteraction } from "../lib/analytics";
import { resolveFooter, type FooterSocials } from "../lib/site-footer";

// White-label site footer, one component for EVERY archetype's public site.
// Config-driven from the venue's own appConfig.footer (addresses, links, socials,
// mailing list), themed entirely from the venue's tokens, and, being resolved
// at render time, it never writes to or overwrites any venue's data. No Assembly
// branding unless a venue explicitly opts in (showAssemblyCredit).

const SOCIAL_ICONS: Partial<Record<keyof FooterSocials, string>> = {
  instagram:
    "M12 2.2c3.2 0 3.6 0 4.8.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.7 3.7 0 0 1-.9 1.38 3.7 3.7 0 0 1-1.38.9c-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.6 2.2 15.2 2.2 12s0-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2m0 5.4a4.4 4.4 0 1 0 0 8.8 4.4 4.4 0 0 0 0-8.8m0 7.26a2.86 2.86 0 1 1 0-5.72 2.86 2.86 0 0 1 0 5.72m5.6-7.44a1.03 1.03 0 1 1-2.06 0 1.03 1.03 0 0 1 2.06 0",
  facebook:
    "M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12",
};

function SocialLink({ href, path, label }: { href: string; path?: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--foreground)] transition hover:opacity-70"
      style={{ backgroundColor: "color-mix(in srgb, var(--foreground) 10%, transparent)" }}
    >
      {path ? (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
          <path d={path} />
        </svg>
      ) : (
        <span className="text-[11px] font-semibold">↗</span>
      )}
    </a>
  );
}

export function SiteFooter({ homeOnly = false }: { homeOnly?: boolean }) {
  const { data } = useVenue();
  const venue = data?.venue;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");

  // On the app-style default venues the footer belongs on the public HOME only,
  // not deep member sub-pages. The web variant (record/festival/radio) passes
  // homeOnly=false so it shows across the whole marketing site.
  if (homeOnly && pathname !== "/") return null;
  if (!venue) return null;
  const f = resolveFooter(venue, venue.appConfig?.footer, new Date());
  if (!f.enabled) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || state === "sending") return;
    pushCtaClick("subscribe");
    // noValidate on the form means a malformed address reaches us instead of
    // being silently blocked by the browser's own type="email" validation,
    // otherwise this check (and the fail event below) would never run.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      pushFormInteraction("fail");
      setState("error");
      return;
    }
    setState("sending");
    try {
      await joinMailingList(venue.id, email.trim(), "footer", undefined, f.mailingListId);
      pushFormInteraction("success");
      setState("done");
      setEmail("");
    } catch {
      pushFormInteraction("fail");
      setState("error");
    }
  };

  const socialEntries = Object.entries(f.socials) as [keyof FooterSocials, string][];

  return (
    <footer
      className="mt-16 border-t px-5 pb-10 pt-10"
      style={{
        borderColor: "var(--border-default)",
        backgroundColor: "color-mix(in srgb, var(--venue-accent, var(--foreground)) 6%, var(--background))",
      }}
    >
      <div className="mx-auto grid max-w-5xl gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {/* Links */}
        {f.links.length > 0 && (
          <nav className="flex flex-col gap-1.5 text-sm">
            {f.links.map((l) => (
              <a key={l.label} href={l.href} className="text-[var(--foreground)] underline-offset-2 transition hover:underline" target={l.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
                {l.label}
              </a>
            ))}
          </nav>
        )}

        {/* Addresses (one column each) */}
        {f.addresses.map((a, i) => (
          <div key={i} className="text-sm">
            {a.label && <div className="mb-1.5 font-semibold text-[var(--foreground)]">{a.label}</div>}
            <address className="not-italic leading-relaxed text-[var(--muted)]">
              {a.lines.map((line, j) => (
                <div key={j}>{line}</div>
              ))}
            </address>
          </div>
        ))}

        {/* Mailing list */}
        {f.showMailingList && (
          <div className="text-sm">
            <div className="mb-2 font-semibold text-[var(--foreground)]">{f.mailingListHeading}</div>
            {state === "done" ? (
              <p className="text-[var(--muted)]">Thanks, you're on the list.</p>
            ) : (
              <form onSubmit={submit} noValidate className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: "var(--border-default)" }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Subscribe"
                  aria-label="Email address"
                  className="min-w-0 flex-1 bg-[var(--surface)] px-3 py-2 text-sm text-[var(--foreground)] outline-none placeholder:text-[var(--muted)]"
                />
                <button
                  type="submit"
                  disabled={state === "sending"}
                  aria-label="Subscribe"
                  className="flex items-center border-0 px-3 text-[var(--accent-fg)] transition hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: "var(--accent)" }}
                >
                  {state === "sending" ? "…" : "→"}
                </button>
              </form>
            )}
            {state === "error" && <p className="mt-1 text-xs text-red-400">Couldn't subscribe, try again.</p>}
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="mx-auto mt-10 flex max-w-5xl flex-col-reverse items-start gap-4 border-t pt-6 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: "var(--border-default)" }}>
        <div className="text-xs text-[var(--muted)]">
          {f.copyright}
          {f.tagline && <span className="ml-2">· {f.tagline}</span>}
          {f.showAssemblyCredit && (
            <span className="ml-2">
              · Powered by <a href="https://asmbly.world" target="_blank" rel="noopener noreferrer" className="underline">Assembly</a>
            </span>
          )}
        </div>
        {socialEntries.length > 0 && (
          <div className="flex items-center gap-2">
            {socialEntries.map(([k, href]) => (
              <SocialLink key={k} href={href} path={SOCIAL_ICONS[k]} label={k} />
            ))}
          </div>
        )}
      </div>
    </footer>
  );
}
